import { useState } from 'react';
import { 
  Building2, ChevronDown, Plus, Check, Settings, Users 
} from 'lucide-react';
import { cn } from '../../lib/utils';

export const WorkspaceSwitcher = ({ 
  workspaces, 
  currentWorkspace, 
  onSwitch, 
  onCreateNew,
  isDark = false
}) => {
  const [isOpen, setIsOpen] = useState(false);

  return (
    <div className="relative">
      {/* Current Workspace Button */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className={cn(
          "w-full flex items-center gap-3 p-3 rounded-lg transition-colors",
          isDark 
            ? "bg-gray-800 hover:bg-gray-700 text-white" 
            : "bg-gray-100 hover:bg-gray-200 text-gray-900"
        )}
        data-testid="workspace-switcher"
      >
        <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center text-white font-bold">
          {currentWorkspace?.name?.charAt(0).toUpperCase() || 'W'}
        </div>
        <div className="flex-1 text-left min-w-0">
          <p className="font-semibold truncate">{currentWorkspace?.name || 'Select Workspace'}</p>
          <p className={cn(
            "text-xs truncate",
            isDark ? "text-gray-400" : "text-gray-500"
          )}>
            {currentWorkspace?.hub_count || 0} hubs • {currentWorkspace?.member_count || 0} members
          </p>
        </div>
        <ChevronDown size={18} className={cn(
          "transition-transform",
          isOpen && "rotate-180"
        )} />
      </button>

      {/* Dropdown */}
      {isOpen && (
        <>
          <div 
            className="fixed inset-0 z-40" 
            onClick={() => setIsOpen(false)} 
          />
          <div className={cn(
            "absolute left-0 right-0 top-full mt-2 rounded-lg shadow-xl border z-50 overflow-hidden",
            isDark 
              ? "bg-gray-800 border-gray-700" 
              : "bg-white border-gray-200"
          )}>
            {/* Workspace List */}
            <div className="max-h-64 overflow-y-auto py-1">
              {workspaces.map(workspace => (
                <button
                  key={workspace.id}
                  onClick={() => {
                    onSwitch(workspace);
                    setIsOpen(false);
                  }}
                  className={cn(
                    "w-full flex items-center gap-3 px-3 py-2 transition-colors",
                    workspace.id === currentWorkspace?.id
                      ? isDark ? "bg-blue-900/30" : "bg-blue-50"
                      : isDark ? "hover:bg-gray-700" : "hover:bg-gray-50"
                  )}
                >
                  <div className={cn(
                    "w-8 h-8 rounded flex items-center justify-center text-white text-sm font-bold",
                    "bg-gradient-to-br from-blue-500 to-purple-600"
                  )}>
                    {workspace.name?.charAt(0).toUpperCase()}
                  </div>
                  <div className="flex-1 text-left min-w-0">
                    <p className={cn(
                      "text-sm font-medium truncate",
                      isDark ? "text-white" : "text-gray-900"
                    )}>
                      {workspace.name}
                    </p>
                    <p className={cn(
                      "text-xs",
                      isDark ? "text-gray-400" : "text-gray-500"
                    )}>
                      {workspace.hub_count || 0} hubs
                    </p>
                  </div>
                  {workspace.id === currentWorkspace?.id && (
                    <Check size={16} className="text-blue-500" />
                  )}
                </button>
              ))}
            </div>

            {/* Divider */}
            <div className={cn(
              "border-t",
              isDark ? "border-gray-700" : "border-gray-200"
            )} />

            {/* Create New Workspace */}
            <button
              onClick={() => {
                onCreateNew?.();
                setIsOpen(false);
              }}
              className={cn(
                "w-full flex items-center gap-2 px-3 py-2 text-sm transition-colors",
                isDark 
                  ? "text-blue-400 hover:bg-gray-700" 
                  : "text-blue-600 hover:bg-gray-50"
              )}
            >
              <Plus size={16} />
              Create New Workspace
            </button>
          </div>
        </>
      )}
    </div>
  );
};

export default WorkspaceSwitcher;
